<template>
  <div>
    <!-- 弹窗 -->
    <el-dialog
      :title="$t('ClaimsManagement.batchUpdateDeductionStatus')"
      :visible.sync="batchDebitStatusDialog"
      width="600px"
      @close="handleClose"
    >
      <!-- 步骤1：上传文件 -->
      <div class="upload-step">
        <h3>1. {{ $t('uploadFile.uploadFileTitle') }} <span class="required">*</span></h3>
        <!-- 自定义上传区域（完全替换 el-upload） -->
        <div class="upload-dragger" @click="triggerFileUpload">
          <div
            :class="{ 'is-dragover': isDragover }"
            class="drag-area"
            @dragover.prevent="isDragover = true"
            @dragleave.prevent="isDragover = false"
            @drop.prevent="handleDrop"
          >
            <!-- 隐藏的原生 input -->
            <input
              ref="referenceUpload"
              accept=".xlsx,.xls,.csv"
              style="display: none"
              type="file"
              @change="handleFileChange($event)"
            >
            <!-- 图标 -->
            <div class="upload-icon-wrapper">
              <i class="el-icon-upload" />
            </div>

            <!-- 状态文字 -->
            <div v-if="!uploadFile">
              <p class="drag-text">{{ $t('ClaimsManagement.dragOrClickToUpload') }}</p>
              <p class="drag-hint">
                {{ $t('ClaimsManagement.fileFormatAndLimit') }},<span style="color: red">
                  {{ $t('ClaimsManagement.max10000Records') }}</span>
              </p>
            </div>
            <!-- 文件已选择时的展示 -->
            <div v-else class="file-selected">
              <i class="el-icon-document" />
              <span class="file-name">{{ uploadFile.name }}</span>
              <span class="file-size">({{ (uploadFile.size / 1024).toFixed(2) }} KB)</span>
              <div class="remove-file" @click.stop="removeFile">
                <i class="el-icon-delete" /> {{ $t('ClaimsManagement.Remove') }}
              </div>
            </div>
          </div>
        </div>

        <!-- 提示信息 -->
        <div class="tip-info">
          <el-tooltip
            :content="$t('ClaimsManagement.downloadTemplateFirst')"
            effect="dark"
            placement="top-start"
          >
            <i class="el-icon-info" />
          </el-tooltip>
          <span>{{ $t('ClaimsManagement.downloadTemplateFirst') }}</span>
          <a class="download-link" href="#" @click.prevent="downloadTemplate">{{ $t('receivablePayable.DownloadTheTemplate') }} (.xlsx)</a>
        </div>
      </div>

      <!-- 底部操作按钮 -->
      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelUpload">{{ $t('customerManagements.cancel') }}</el-button>
        <el-button :loading="uploading" type="primary" @click="confirmUpload">{{
          $t('ClaimsManagement.confirmUpload')
        }}</el-button>
      </span>
    </el-dialog>

    <!-- 上传结果弹窗 -->
    <el-dialog
      :title="$t('ClaimsManagement.uploadResult')"
      :visible.sync="dialogVisible"
      append-to-body
      width="800px"
      @close="handleCloseResult"
    >
      <!-- 统计卡片区域 -->
      <div class="stats-cards">
        <div class="card blue-card">
          <div class="number">{{ failTipData.totalCount }}</div>
          <div class="label">{{ $t('ClaimsManagement.Remove') }}</div>
        </div>
        <div class="card green-card">
          <div class="number">{{ failTipData.successCount }}</div>
          <div class="label">{{ $t('ClaimsManagement.successCount') }}</div>
        </div>
        <div class="card red-card">
          <div class="number">{{ failTipData.failCount }}</div>
          <div class="label">{{ $t('ClaimsManagement.failCount') }}</div>
        </div>
      </div>
      <!-- 失败明细表格 -->
      <div class="fail-detail">
        <div v-if="failTipData.failCount > 0" class="detail-header">
          <div>{{ $t('ClaimsManagement.failureDetails') }}</div>
          <el-link type="primary" @click="handleDownload(1)">{{ $t('ClaimsManagement.fixAndReupload') }}
          </el-link>
        </div>
        <div v-else style="text-align: center; color: #909399; padding: 20px">
          {{ $t('ClaimsManagement.noFailureData') }}
        </div>
      </div>
      <!-- 底部操作按钮 -->
      <span slot="footer" class="dialog-footer">
        <el-button
          v-if="failTipData.failCount > 0"
          style="margin-right: 20px"
          @click="handleDownload(2)"
        >{{ $t('ClaimsManagement.downloadFailureDetails') }}</el-button>
        <el-button type="primary" @click="dialogVisible = false">{{
          $t('orderExport.close')
        }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { importForUpdateStatus } from '@/api/definiteResponsibilityAndPenalty';

export default {
  name: 'BatchUpdateDialog',
  data() {
    return {
      batchDebitStatusDialog: false,
      uploading: false,
      // 存储上传的文件对象
      uploadFile: null,
      isDragover: false,

      dialogVisible: false,
      // 统计结果数据
      stats: {
        total: 0,
        success: 0,
        fail: 0
      },
      // 失败列表数据
      failList: [],
      failTipData: {
        errorFileUrl: '',
        failCount: 0,
        successCount: 0,
        totalCount: 0
      }
    };
  },
  methods: {
    // 1. 点击自定义区域，触发隐藏 input 的点击
    triggerFileUpload() {
      if (!this.uploadFile) {
        this.$refs.referenceUpload.click();
      }
    },

    // 2. 拖拽文件上传
    handleDrop(e) {
      this.isDragover = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    // 3. 通过 input 选择文件
    handleFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.processFile(file);
      }
      // 清空 input，防止选择同一个文件不触发 change
      e.target.value = '';
    },

    // 4. 处理文件校验并赋值
    processFile(file) {
      const validTypes = ['.xlsx', '.xls', '.csv'];
      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      // 校验格式
      if (!validTypes.includes(extension)) {
        this.$message.error(this.$t('ClaimsManagement.fileFormatError'));
        return;
      }

      // 检验大小 (这里限制 10MB)
      if (file.size / 1024 / 1024 > 10) {
        this.$message.error(this.$t('ClaimsManagement.fileSizeExceed'));
        return;
      }
      this.uploadFile = file;
    },

    // 5. 移除文件
    removeFile() {
      this.uploadFile = null;
      this.$refs.referenceUpload.value = ''; // 清空 input 值
    },

    // 6. 确认上传按钮
    async confirmUpload() {
      if (!this.uploadFile) {
        this.$message.warning(this.$t('ClaimsManagement.pleaseUploadFileFirst'));
        return;
      }

      this.uploading = true;

      // 组装 FormData
      const formData = new FormData();
      formData.append('file', this.uploadFile);
      // 如果还需要其他参数，可以追加：formData.append('type', 'debit');

      try {
        const result = await importForUpdateStatus(formData);

        if (result.data) {
          this.failTipData = result.data;
          // 关闭上传弹窗，打开结果弹窗
          this.batchDebitStatusDialog = false;
          this.dialogVisible = true;
          this.removeFile(); // 清空文件
          this.$emit('emitSearch');
        }
      } catch (error) {
        console.log(error, 'error');
      } finally {
        this.uploading = false;
      }
    },
    // 7. 下载模板
    downloadTemplate() {
      let templateUrl = '';
      if (this.$i18n.locale === 'zh') {
        templateUrl =
          'https://express-v2-1313947203.cos.sa-saopaulo.myqcloud.com/template/lm/excel/fine_status_import_template_zh.xlsx';
      } else {
        templateUrl =
          'https://express-v2-1313947203.cos.sa-saopaulo.myqcloud.com/template/lm/excel/fine_status_import_template_pu.xlsx';
      }
      // 方案一（推荐）：使用动态 a 标签，利用 download 属性强制下载
      const link = document.createElement('a');
      link.href = templateUrl;
      // 设置 download 属性，可以防止浏览器直接预览 Excel，而是触发下载行为
      // 如果后端文件名已经固定，也可以省略 download 属性里的文件名
      link.download = '批量更新扣款状态模板.xlsx';

      // 为了防止在部分浏览器上直接跳转，可以将 a 标签隐藏并添加到 body 中
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.$message.success(this.$t('ClaimsManagement.downloadSuccess'));
    },

    // 其他方法保持不变
    handleDownload(val) {
      if (val === 1) {
        window.open(this.failTipData.errorFileUrl, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = this.failTipData.errorFileUrl;
        link.download = '批量更新扣款状态模板.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.$message.success(this.$t('ClaimsManagement.downloadSuccess'));
      }
    },
    handleClose() {
      this.uploading = false;
      this.removeFile();
      this.isDragover = false;
    },
    handleCloseResult() {
      this.dialogVisible = false;
    },
    cancelUpload() {
      this.batchDebitStatusDialog = false;
      this.removeFile();
    },
    showBatchDebitStatusDialog() {
      this.batchDebitStatusDialog = true;
    }
  }
};
</script>

<style scoped>
/* ---------- 原样式优化 ---------- */
.upload-step {
  padding-bottom: 20px;
}

.upload-step h3 {
  margin-top: 0;
  font-weight: 600;
  color: #303133;
}

.required {
  color: #f56c6c;
  margin-left: 4px;
}

/* 自定义上传区域样式 */
.upload-dragger {
  margin-top: 16px;
  cursor: pointer;
}

.drag-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 180px;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  background-color: #f9fbfd;
  transition: all 0.3s ease;
  position: relative;
}

.drag-area:hover,
.drag-area.is-dragover {
  border-color: #409eff;
  background-color: #ecf5ff;
}

/* 图标圆形背景 */
.upload-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #e6f0ff;
  margin-bottom: 12px;
}

.upload-icon-wrapper i {
  font-size: 24px;
  color: #409eff;
}

.drag-text {
  font-size: 16px;
  color: #303133;
  margin: 0 0 8px 0;
}

.drag-hint {
  font-size: 14px;
  color: #909399;
  margin: 0;
}

/* 文件选中后的展示 */
.file-selected {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  padding: 10px 16px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e4e7ed;
}

.file-selected .el-icon-document {
  font-size: 20px;
  color: #409eff;
}

.file-name {
  font-weight: 600;
  color: #303133;
}

.file-size {
  font-size: 12px;
  color: #909399;
}

.remove-file {
  margin-left: 16px;
  font-size: 13px;
  color: #f56c6c;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 2px;
}

.remove-file:hover {
  text-decoration: underline;
}

/* 提示信息行 */
.tip-info {
  display: flex;
  align-items: center;
  margin-top: 20px;
  font-size: 14px;
  color: #606266;
}

.tip-info i {
  margin-right: 8px;
  color: #909399;
}

.download-link {
  margin-left: 8px;
  color: #409eff;
  text-decoration: none;
}

.download-link:hover {
  text-decoration: underline;
}

.dialog-footer {
  text-align: right;
}

/* ------- 结果弹窗样式 (保持与你提供的一致) ------- */
.stats-cards {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
}

.card {
  flex: 1;
  padding: 20px 16px;
  border-radius: 8px;
  text-align: center;
  font-size: 14px;
}

.card .number {
  font-size: 32px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 8px;
}

.card .label {
  color: #606266;
}

.blue-card {
  background-color: #f0f7ff;
  color: #1890ff;
}

.green-card {
  background-color: #f0fdf5;
  color: #13c2c2;
}

.red-card {
  background-color: #fff1f0;
  color: #f5222d;
}

.summary {
  padding: 16px;
  background-color: #f9fbfd;
  border-radius: 6px;
  margin-bottom: 24px;
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
}

.summary strong {
  color: #303133;
  font-weight: 600;
}

.fail-detail {
  margin-bottom: 24px;
}

.detail-header {
  margin-bottom: 12px;
  font-weight: 600;
  color: #303133;
}

.error-text {
  color: #f5222d;
  font-weight: 500;
}
</style>
